import type { ChartGroup } from '@integratio/core';
import { formatPercent } from '../../lib/format';

interface Props {
  groups: ChartGroup[];
  total: number;
  /** Limita a lista por grupo (usado apenas na tela). */
  limit?: number;
  showMetrics?: boolean;
  /** Exibe a lista de nomes por grupo. Quando false, mostra apenas grupo + métricas. */
  showNames?: boolean;
}

/** Painel de legenda: trilho vertical, marcadores por grupo e lista de stakeholders. */
export function IsoLegend({ groups, total, limit = Infinity, showMetrics = true, showNames = true }: Props): JSX.Element {
  return (
    <div className="relative pl-6">
      <span aria-hidden className="absolute inset-y-1 left-1.5 w-px bg-brand-line" />
      <ul className="space-y-5">
        {groups.map((group) => {
          const empty = group.count === 0;
          const color = group.color;
          const visible = group.stakeholders.slice(0, limit === Infinity ? undefined : limit);
          const hidden = group.stakeholders.length - visible.length;

          return (
            <li key={group.key} className="avoid-break relative">
              <span
                aria-hidden
                className="absolute -left-6 top-1 h-3 w-3 rounded-full ring-4 ring-white"
                style={{ backgroundColor: color }}
              />
              <div className="flex flex-wrap items-baseline gap-x-2">
                <p className="text-[12px] font-bold uppercase tracking-wide" style={{ color }}>
                  {group.name}
                  {group.subtitle ? ` · ${group.subtitle}` : ''}
                </p>
                {showMetrics && (
                  <p className="text-[11px] font-semibold tabular-nums text-brand-slate">
                    {formatPercent(group.percentage)} · {group.count} de {total}
                  </p>
                )}
              </div>

              {empty ? (
                <p className="mt-1 text-[11px] italic text-brand-slate/80">{group.emptyText}</p>
              ) : showNames ? (
                <ul className="mt-1.5 space-y-0.5">
                  {visible.map((item) => (
                    <li key={item.id} className="leading-tight">
                      <span className="text-[10.5px] font-semibold" style={{ color }}>
                        {item.nome}
                      </span>
                      {(item.cargo || item.empresaInstituicao) && (
                        <span className="block text-[9px] text-brand-slate/80">
                          {[item.cargo, item.empresaInstituicao].filter(Boolean).join(' — ')}
                        </span>
                      )}
                    </li>
                  ))}
                  {hidden > 0 && (
                    <li className="text-[11px] font-semibold text-brand-slate">+ {hidden} stakeholder(s)</li>
                  )}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
